import { useState } from 'react'
import { Box, Tabs, Tab, Typography } from '@mui/material'
import { Favorite as ServiceIcon } from '@mui/icons-material'
import { PageHeader } from '../components/shared/PageHeader'
import { CaseList } from '../features/cases/CaseList'
import { CaseForm } from '../features/cases/CaseForm'
import { CaseDetail } from '../features/cases/CaseDetail'
import { TaskList } from '../features/tasks/TaskList'
import { TaskForm } from '../features/tasks/TaskForm'
import { useCases } from '../lib/hooks/useCases'
import { useTasks } from '../lib/hooks/useTasks'
import { themeColors } from '../app/layout/theme'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && children}
    </Box>
  )
}

export default function ServicePage() {
  const [tabValue, setTabValue] = useState(0)
  const [showCaseForm, setShowCaseForm] = useState(false)
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)

  const { cases, isLoading: casesLoading, createCase, searchCases, searchResults } = useCases()
  const { tasks, isLoading: tasksLoading, createTask } = useTasks()

  const displayCases = searchResults.length > 0 ? searchResults : cases
  const selectedCase = selectedCaseId ? cases.find((c) => c.id === selectedCaseId) : null

  const tabs = ['Cases', 'Contacts', 'Accounts', 'Analytics', 'Knowledge']

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Service"
      />

      <Box
        sx={{
          backgroundColor: themeColors.surface,
          borderBottom: `1px solid ${themeColors.border}`,
          px: 3,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, v) => { setTabValue(v); setSelectedCaseId(null) }}
          sx={{
            '& .MuiTab-root': { color: themeColors.text.secondary, '&.Mui-selected': { color: themeColors.primary } },
            '& .MuiTabs-indicator': { backgroundColor: themeColors.primary },
          }}
        >
          {tabs.map((tab) => <Tab key={tab} label={tab} />)}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TabPanel value={tabValue} index={0}>
          {selectedCase ? (
            <CaseDetail caseRecord={selectedCase} onEdit={() => setShowCaseForm(true)} />
          ) : (
            <CaseList
              cases={displayCases}
              isLoading={casesLoading}
              onNewClick={() => setShowCaseForm(true)}
              onSearch={(q) => q.trim() && searchCases(q)}
              onCaseClick={(c) => setSelectedCaseId(c.id)}
            />
          )}
        </TabPanel>

        {/* Tasks as a "To Do" panel within Accounts tab placeholder */}
        <TabPanel value={tabValue} index={1}>
          <TaskList
            tasks={tasks}
            isLoading={tasksLoading}
            onNewClick={() => setShowTaskForm(true)}
          />
        </TabPanel>

        {[2, 3, 4].map((idx) => (
          <TabPanel key={idx} value={tabValue} index={idx}>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: themeColors.text.secondary, fontSize: '14px' }}>
                {tabs[idx]} — coming soon
              </Typography>
            </Box>
          </TabPanel>
        ))}
      </Box>

      <CaseForm open={showCaseForm} onClose={() => setShowCaseForm(false)} onSubmit={(d) => { createCase(d); setShowCaseForm(false) }} />
      <TaskForm open={showTaskForm} onClose={() => setShowTaskForm(false)} onSubmit={(d) => { createTask(d); setShowTaskForm(false) }} />
    </Box>
  )
}
